import {
  BatteryIcon,
  BoardIcon,
  ButtonIcon,
  CapacitorIcon,
  type CategoryIcon,
  ChemistryIcon,
  ChipIcon,
  ConnectorIcon,
  ControllerIcon,
  CrystalIcon,
  DefaultIcon,
  DiodeIcon,
  EnclosureIcon,
  FanIcon,
  FuseIcon,
  HeatsinkIcon,
  InductorIcon,
  KnobIcon,
  LedBulbIcon,
  LedDriverIcon,
  LedFixtureIcon,
  LedIcon,
  LedStripIcon,
  MotorIcon,
  MultimeterIcon,
  PowerIcon,
  RelayIcon,
  ResistorIcon,
  SensorIcon,
  SpeakerIcon,
  SwitchIcon,
  TerminalIcon,
  ToolsIcon,
  TransformerIcon,
  TransistorIcon,
  TriacIcon,
  WireIcon,
  WirelessIcon,
  ZenerIcon,
} from "./category-icons"

export type CategoryStyle = {
  Icon: CategoryIcon
  /** soft tinted background (used in icon tile) */
  bg: string
  /** text-color utility (drives currentColor of the SVG stroke) */
  color: string
}

// NOTE: class names are written out in full so Tailwind's JIT scanner can
// statically discover them. Do not template-string the colour name.
const STYLES = {
  diode: {
    Icon: DiodeIcon,
    bg: "bg-cyan-500/8 dark:bg-cyan-400/8",
    color: "text-cyan-500 dark:text-cyan-400",
  },
  zener: {
    Icon: ZenerIcon,
    bg: "bg-red-500/8 dark:bg-red-400/8",
    color: "text-red-500 dark:text-red-400",
  },
  triac: {
    Icon: TriacIcon,
    bg: "bg-rose-500/8 dark:bg-rose-400/8",
    color: "text-rose-500 dark:text-rose-400",
  },
  transistor: {
    Icon: TransistorIcon,
    bg: "bg-sky-500/8 dark:bg-sky-400/8",
    color: "text-sky-500 dark:text-sky-400",
  },
  resistor: {
    Icon: ResistorIcon,
    bg: "bg-emerald-500/8 dark:bg-emerald-400/8",
    color: "text-emerald-500 dark:text-emerald-400",
  },
  capacitor: {
    Icon: CapacitorIcon,
    bg: "bg-amber-500/8 dark:bg-amber-400/8",
    color: "text-amber-500 dark:text-amber-400",
  },
  inductor: {
    Icon: InductorIcon,
    bg: "bg-pink-500/8 dark:bg-pink-400/8",
    color: "text-pink-500 dark:text-pink-400",
  },
  chip: {
    Icon: ChipIcon,
    bg: "bg-indigo-500/8 dark:bg-indigo-400/8",
    color: "text-indigo-500 dark:text-indigo-400",
  },
  led: {
    Icon: LedIcon,
    bg: "bg-orange-500/8 dark:bg-orange-400/8",
    color: "text-orange-500 dark:text-orange-400",
  },
  ledStrip: {
    Icon: LedStripIcon,
    bg: "bg-amber-500/8 dark:bg-amber-400/8",
    color: "text-amber-500 dark:text-amber-400",
  },
  ledBulb: {
    Icon: LedBulbIcon,
    bg: "bg-yellow-500/8 dark:bg-yellow-400/8",
    color: "text-yellow-500 dark:text-yellow-400",
  },
  ledDriver: {
    Icon: LedDriverIcon,
    bg: "bg-violet-500/8 dark:bg-violet-400/8",
    color: "text-violet-500 dark:text-violet-400",
  },
  ledFixture: {
    Icon: LedFixtureIcon,
    bg: "bg-orange-500/8 dark:bg-orange-400/8",
    color: "text-orange-500 dark:text-orange-400",
  },
  crystal: {
    Icon: CrystalIcon,
    bg: "bg-blue-500/8 dark:bg-blue-400/8",
    color: "text-blue-500 dark:text-blue-400",
  },
  relay: {
    Icon: RelayIcon,
    bg: "bg-teal-500/8 dark:bg-teal-400/8",
    color: "text-teal-500 dark:text-teal-400",
  },
  fuse: {
    Icon: FuseIcon,
    bg: "bg-red-500/8 dark:bg-red-400/8",
    color: "text-red-500 dark:text-red-400",
  },
  switchEl: {
    Icon: SwitchIcon,
    bg: "bg-slate-500/8 dark:bg-slate-400/8",
    color: "text-slate-600 dark:text-slate-300",
  },
  button: {
    Icon: ButtonIcon,
    bg: "bg-slate-500/8 dark:bg-slate-400/8",
    color: "text-slate-600 dark:text-slate-300",
  },
  connector: {
    Icon: ConnectorIcon,
    bg: "bg-fuchsia-500/8 dark:bg-fuchsia-400/8",
    color: "text-fuchsia-500 dark:text-fuchsia-400",
  },
  terminal: {
    Icon: TerminalIcon,
    bg: "bg-fuchsia-500/8 dark:bg-fuchsia-400/8",
    color: "text-fuchsia-500 dark:text-fuchsia-400",
  },
  wire: {
    Icon: WireIcon,
    bg: "bg-lime-500/8 dark:bg-lime-400/8",
    color: "text-lime-600 dark:text-lime-400",
  },
  battery: {
    Icon: BatteryIcon,
    bg: "bg-green-500/8 dark:bg-green-400/8",
    color: "text-green-500 dark:text-green-400",
  },
  speaker: {
    Icon: SpeakerIcon,
    bg: "bg-purple-500/8 dark:bg-purple-400/8",
    color: "text-purple-500 dark:text-purple-400",
  },
  sensor: {
    Icon: SensorIcon,
    bg: "bg-cyan-500/8 dark:bg-cyan-400/8",
    color: "text-cyan-500 dark:text-cyan-400",
  },
  fan: {
    Icon: FanIcon,
    bg: "bg-sky-500/8 dark:bg-sky-400/8",
    color: "text-sky-500 dark:text-sky-400",
  },
  transformer: {
    Icon: TransformerIcon,
    bg: "bg-pink-500/8 dark:bg-pink-400/8",
    color: "text-pink-500 dark:text-pink-400",
  },
  heatsink: {
    Icon: HeatsinkIcon,
    bg: "bg-zinc-500/8 dark:bg-zinc-400/8",
    color: "text-zinc-600 dark:text-zinc-300",
  },
  board: {
    Icon: BoardIcon,
    bg: "bg-violet-500/8 dark:bg-violet-400/8",
    color: "text-violet-500 dark:text-violet-400",
  },
  wireless: {
    Icon: WirelessIcon,
    bg: "bg-blue-500/8 dark:bg-blue-400/8",
    color: "text-blue-500 dark:text-blue-400",
  },
  motor: {
    Icon: MotorIcon,
    bg: "bg-amber-500/8 dark:bg-amber-400/8",
    color: "text-amber-600 dark:text-amber-400",
  },
  multimeter: {
    Icon: MultimeterIcon,
    bg: "bg-emerald-500/8 dark:bg-emerald-400/8",
    color: "text-emerald-500 dark:text-emerald-400",
  },
  tools: {
    Icon: ToolsIcon,
    bg: "bg-stone-500/8 dark:bg-stone-400/8",
    color: "text-stone-600 dark:text-stone-300",
  },
  chemistry: {
    Icon: ChemistryIcon,
    bg: "bg-green-500/8 dark:bg-green-400/8",
    color: "text-green-600 dark:text-green-400",
  },
  enclosure: {
    Icon: EnclosureIcon,
    bg: "bg-stone-500/8 dark:bg-stone-400/8",
    color: "text-stone-600 dark:text-stone-300",
  },
  power: {
    Icon: PowerIcon,
    bg: "bg-violet-500/8 dark:bg-violet-400/8",
    color: "text-violet-500 dark:text-violet-400",
  },
  knob: {
    Icon: KnobIcon,
    bg: "bg-rose-500/8 dark:bg-rose-400/8",
    color: "text-rose-500 dark:text-rose-400",
  },
  controller: {
    Icon: ControllerIcon,
    bg: "bg-teal-500/8 dark:bg-teal-400/8",
    color: "text-teal-500 dark:text-teal-400",
  },
  // Roots reuse archetype Icons but get their own colour treatment.
  rootActive: {
    Icon: ChipIcon,
    bg: "bg-cyan-500/8 dark:bg-cyan-400/8",
    color: "text-cyan-500 dark:text-cyan-400",
  },
  rootPassive: {
    Icon: ResistorIcon,
    bg: "bg-emerald-500/8 dark:bg-emerald-400/8",
    color: "text-emerald-500 dark:text-emerald-400",
  },
  rootMisc: {
    Icon: ConnectorIcon,
    bg: "bg-fuchsia-500/8 dark:bg-fuchsia-400/8",
    color: "text-fuchsia-500 dark:text-fuchsia-400",
  },
  rootKits: {
    Icon: BoardIcon,
    bg: "bg-violet-500/8 dark:bg-violet-400/8",
    color: "text-violet-500 dark:text-violet-400",
  },
  rootLighting: {
    Icon: LedFixtureIcon,
    bg: "bg-orange-500/8 dark:bg-orange-400/8",
    color: "text-orange-500 dark:text-orange-400",
  },
} satisfies Record<string, CategoryStyle>

const DEFAULT_STYLE: CategoryStyle = {
  Icon: DefaultIcon,
  bg: "bg-slate-500/8 dark:bg-slate-400/8",
  color: "text-slate-500 dark:text-slate-400",
}

/**
 * Substring patterns matched in priority order against the lowercased
 * category name. Most specific first wins. Covers Russian + Ukrainian
 * spellings since the live catalogue is Russian but UI is Ukrainian.
 */
const PATTERNS: Array<[RegExp, CategoryStyle]> = [
  // --- LED specifics (must beat the broader /светодиод/ pattern) ---
  [/led.?драйвер|драйвер.*светодиод|драйвер.*led/i, STYLES.ledDriver],
  [/лента.*светодиод|светодиод.*лента|led.*лента|лента.*led|неон|гирлянд|цветомузык|набор.*led|led.*ленты/i, STYLES.ledStrip],
  [/лампа.*светодиод|лампы.*светодиод|светодиод.*лампа|лампа.*led|led.*лампа/i, STYLES.ledBulb],
  [/светильник|освещен|освітлен|led.?панел|led.?линейк|профил.*led/i, STYLES.ledFixture],
  [/модул.*светодиод|светодиод.*модул|модул.*220vac|модул.*12v.*реклам|светодиодные.*приборы|светодиодные.*и.*жки|жки.*индикатор/i, STYLES.led],

  // --- Chip-vs-throughhole resistor/capacitor (early to win over the broader /резистор/ /конденсатор/) ---
  [/чип.?конденсатор|smd.?конденсатор/i, STYLES.capacitor],
  [/чип.?резистор|smd.?резистор/i, STYLES.resistor],

  // --- Sensors / dimmers / regulators (specific RadioLED naming) ---
  [/сенсор.*датчик.*диммер|датчик.*диммер|сенсор.*диммер/i, STYLES.sensor],
  [/диммер|терморегулятор|влагорегулятор|управление.*контроллер|реглятор|регулятор|контроллер|контролер/i, STYLES.controller],
  [/автоэлектрон|авто.?электрон|автомобил.*электр|автоматик/i, STYLES.controller],

  // --- Active components ---
  [/диодны?е.?мост|диодный.?мост|мост.*диод/i, STYLES.diode],
  [/диод|діод/i, STYLES.diode],
  [/стабилитрон|стабілітрон|зенер|супрессор|tvs/i, STYLES.zener],
  [/триак|симистор|тиристор/i, STYLES.triac],
  [/транзистор|igbt|mosfet/i, STYLES.transistor],

  // --- LED (after the LED-specific group above) ---
  [/светодиод|світлодіод|\bled\b/i, STYLES.led],

  // --- Crystals / oscillators / filters ---
  [/кварц|crystal|резонатор|фильтр|фільтр/i, STYLES.crystal],

  // --- Microschemes / programmers / sockets / Arduino-class boards ---
  [/микросхем|мікросхем|программатор|programmer|panelk|панельк/i, STYLES.chip],
  [/arduino|esp\d|stm32|raspberry|расширения|плата.*расшир/i, STYLES.board],
  [/макетн|breadboard|pcb|текстолит|текстоліт|плат.*radio|radio-?kit/i, STYLES.board],
  [/wi.?fi|bluetooth|\bbt\b|gsm|вч|свч|антенн|wireless|радио.?сигнал/i, STYLES.wireless],

  // --- Active-USSR & misc semiconductor box: route to chip ---
  [/компонент.*актив/i, STYLES.chip],

  // --- Passives ---
  [/конденсатор|capacitor|ионистор/i, STYLES.capacitor],
  [/индуктивн|індуктивн|катушк|котушк|дросел/i, STYLES.inductor],
  [/трансформатор/i, STYLES.transformer],
  [/ручк|knob/i, STYLES.knob],
  [/варистор/i, STYLES.resistor],
  [/терморезистор|термистор|ntc|ptc/i, STYLES.sensor],
  [/потенциометр|реостат/i, STYLES.knob],
  [/резистор|компонент.*пасс?ив/i, STYLES.resistor],

  // --- Switching, protection & interconnect ---
  [/реле|relay/i, STYLES.relay],
  [/предохранител|запобіжник|термопредохранител|термостат|fuse/i, STYLES.fuse],
  [/тумблер|переключател|перемикач|выключател|вимикач|rocker|switch|клавишн/i, STYLES.switchEl],
  [/кнопк|тактов|button|микрокнопк/i, STYLES.button],
  [/клеммник|клемм/i, STYLES.terminal],
  [/разъ[её]м|роз'?єм|connector|джек|usb|jumper|перемычк|штыр|зажим.*крокодил|крокодил/i, STYLES.connector],
  [/провод|провід|кабел|шнур|шлейф|термоусадк|наконечник/i, STYLES.wire],

  // --- Power / batteries / cooling / enclosures / acoustics / sensors / motors / measurement / chemistry ---
  [/аккумулятор|акумулятор|батарей|battery|зарядк|батарейн.*модул/i, STYLES.battery],
  [/вентилятор|cooler|кулер|охлажден/i, STYLES.fan],
  [/радиатор|радіатор|heatsink|втулк|прокладк/i, STYLES.heatsink],
  [/датчик|сенсор|sensor/i, STYLES.sensor],
  [/акустик|колонк|динамик|speaker|звук|саб(вуфер)?/i, STYLES.speaker],
  [/двигател|мотор\b|редуктор|драйвер.*мотор|шагов/i, STYLES.motor],
  [/измерител|мультиметр|осцилл|осцило|тестер|вольтметр|амперметр/i, STYLES.multimeter],
  [/преобразовател|конвертер|converter|inverter|инвертер|инвертор/i, STYLES.power],
  [/блок.*питан|блок.*живлен|питан|живлен|герметичн.*ip|металлическ.*ip|адаптерн|карандаш|электротовар/i, STYLES.power],
  // standalone "Свет" / "Світло" (RU/UA "Light" — used as a sub-tree label)
  [/^\s*свет\s*$|^\s*світло\s*$/i, STYLES.ledFixture],
  [/корпус|enclosure|крепеж|упаковк|box|шасси/i, STYLES.enclosure],
  [/инструмент|інструмент|паяльн|жал|tool|оборудовани/i, STYLES.tools],
  [/химия|хімія|флюс|припой/i, STYLES.chemistry],
  [/лазерн.*головк|лазерн|laser/i, STYLES.chip],
  [/запчаст|cd|dvd|свч/i, STYLES.tools],

  // --- Roots ---
  [/радиокомпонент.*актив|радіокомпонент.*актив/i, STYLES.rootActive],
  [/радиокомпонент.*пасс?ив|радіокомпонент.*пасив/i, STYLES.rootPassive],
  [/радиокомпонент.*разн|радіокомпонент.*різн/i, STYLES.rootMisc],
  [/радиоконструктор|радіоконструктор/i, STYLES.rootKits],
  [/светодиодное.*освещ|світлодіодн.*освіт/i, STYLES.rootLighting],
]

export function getCategoryStyle(name: string | null | undefined): CategoryStyle {
  if (!name) return DEFAULT_STYLE
  const n = name.trim().toLowerCase()
  for (const [re, s] of PATTERNS) {
    if (re.test(n)) return s
  }
  return DEFAULT_STYLE
}
